/**
 * useNeedProducts — "See all" for one Recommendation-Plan Need (SCRUM-187).
 *
 * The sheet used to call `useMarketplaceSearch`, i.e. the LEXICAL
 * `/marketplace/search`, with the Need's label as the query. The chip beside it
 * used SEMANTIC retrieval (embeddings). Because the catalogue is Russian, an
 * English label like "Plumbing fixtures" matched nothing lexically even when the
 * chip had just shown four products — measured: `q="Plumbing fixtures"` → 0
 * items, `q="Смеситель"` → 5.
 *
 * This hook posts the Need itself to `/chat/needs/products`, which runs the
 * SAME `retrieve_for_need` the chip does, only with a larger limit. One
 * retrieval seam, so the two surfaces cannot drift apart again.
 *
 * Sorting stays client-side on purpose: "relevance" IS the semantic order the
 * server returned, and re-sorting by price must not silently reorder that
 * ranking on the server where the chip would then disagree.
 */
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";
import type { ProductSearchItem } from "@/features/marketplace/useMarketplaceBrowse";
import type { Need } from "@/features/chat/useChatStream";
import type { SortValue } from "@/features/marketplace/SortSelect";

/** Wire shape — identical to the chat card frame, so the backend has ONE card
 * builder. `price_kgs` arrives as a Decimal-encoded STRING (JSON has no
 * decimal type); the marketplace `ProductCard` expects a number. */
interface NeedProductCard extends Omit<ProductSearchItem, "price_kgs"> {
  price_kgs: string | number | null;
}

interface NeedProductsResponse {
  items: NeedProductCard[];
}

function toSearchItem(card: NeedProductCard): ProductSearchItem {
  const raw = card.price_kgs;
  const price =
    raw === null || raw === undefined
      ? null
      : typeof raw === "number"
        ? raw
        : Number.parseFloat(raw);
  return {
    ...card,
    price_kgs: price !== null && Number.isFinite(price) ? price : null,
  };
}

function sortItems(items: ProductSearchItem[], sort: SortValue): ProductSearchItem[] {
  if (sort === "relevance") return items; // the server's semantic ranking
  const withPrice = [...items];
  withPrice.sort((a, b) => {
    // Priceless items sink to the bottom either way rather than sorting as 0.
    if (a.price_kgs === null) return 1;
    if (b.price_kgs === null) return -1;
    return sort === "price_asc"
      ? a.price_kgs - b.price_kgs
      : b.price_kgs - a.price_kgs;
  });
  return withPrice;
}

export interface UseNeedProductsArgs {
  /** The Need to expand, or null while the sheet is closed (query disabled). */
  need: Need | null;
  language: string;
  sort: SortValue;
  inStockOnly: boolean;
  limit?: number;
}

export function useNeedProducts({
  need,
  language,
  sort,
  inStockOnly,
  limit = 24,
}: UseNeedProductsArgs): {
  items: ProductSearchItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    // `sort` is NOT in the key — it is applied client-side, so re-sorting must
    // not re-fetch (and re-embed, which costs a provider call).
    queryKey: ["chat", "need-products", need?.need_id ?? null, language, inStockOnly, limit],
    enabled: need !== null,
    staleTime: 60_000,
    queryFn: () =>
      apiClient.post<NeedProductsResponse, Record<string, unknown>>(
        "/chat/needs/products",
        { need, language, limit, in_stock_only: inStockOnly },
        // Guest-allowed route (contract); a member still sends the member token.
        { allowGuest: true },
      ),
  });

  const items = (query.data?.items ?? []).map(toSearchItem);
  return {
    items: sortItems(items, sort),
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
